import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MarketingLayout from './components/layout/MarketingLayout';
import PortalLayout from './components/layout/PortalLayout';
import ScrollManager from './components/common/ScrollManager';
import ErrorBoundary from './components/common/ErrorBoundary';
import ROUTES from './utils/routes';

/**
 * Route-level code splitting.
 *
 * Every page is loaded on demand so the marketing site ships a small initial
 * bundle and the future portal or assistant surface can grow without inflating
 * the first paint for ordinary visitors.
 */
const HomePage = lazy(() => import('./pages/HomePage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const IndustriesPage = lazy(() => import('./pages/IndustriesPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));
const PortalPlaceholderPage = lazy(() => import('./pages/PortalPlaceholderPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

/**
 * Application root.
 *
 * Two shells are registered from the start: the public marketing shell and a
 * reserved portal shell for future authenticated features. Hosting must map
 * unknown paths back to index.html for client-side routing. On the S3 +
 * CloudFront deployment that is the distribution's custom error response
 * (403 and 404 -> /index.html with a 200 status). See the Deployment section
 * of README.md.
 */
const App = () => (
  <>
    <ScrollManager />
    <ErrorBoundary>
      <Routes>
        <Route element={<MarketingLayout />}>
          <Route path={ROUTES.home} element={<HomePage />} />
          <Route path={ROUTES.services} element={<ServicesPage />} />
          <Route path={ROUTES.industries} element={<IndustriesPage />} />
          <Route path={ROUTES.howItWorks} element={<HowItWorksPage />} />
          <Route path={ROUTES.about} element={<AboutPage />} />
          <Route path={ROUTES.contact} element={<ContactPage />} />
          <Route path={ROUTES.privacy} element={<PrivacyPolicyPage />} />
          <Route path={ROUTES.terms} element={<TermsOfUsePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Reserved for future authenticated features. No auth is implemented. */}
        <Route path={ROUTES.portal} element={<PortalLayout />}>
          <Route index element={<PortalPlaceholderPage />} />
          <Route path="*" element={<Navigate to={ROUTES.portal} replace />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  </>
);

export default App;
